import React, { useState } from 'react';
import { api, DatabasePreflight } from '../api';
import { Callout, Checkbox, Dialog, Input, LogPanel, Section, Subsection, Table } from './primitives';
import { ConfirmDialog, Field, LoadingRows, StatusBadge } from './ui';

interface DatabasePreflightCardProps {
  deploymentId: string;
  preflight: DatabasePreflight | null;
  loading?: boolean;
  onRefresh?: () => void;
  canManageDeployments?: boolean;
}

export const DatabasePreflightCard: React.FC<DatabasePreflightCardProps> = ({
  deploymentId,
  preflight,
  loading = false,
  onRefresh,
  canManageDeployments = true,
}) => {
  const [expandedSql, setExpandedSql] = useState<Record<string, boolean>>({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const toggleSql = (id: string) => {
    setExpandedSql((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleApprove = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api.approveDatabasePreflight(deploymentId, { note: approvalNote });
      setShowApprovalModal(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'The approval was not saved.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api.rejectDatabasePreflight(deploymentId, { reason: rejectReason });
      setShowRejectModal(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'The rejection was not saved.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLog = () => {
    if (preflight?.raw_dry_run_log) {
      void navigator.clipboard?.writeText(preflight.raw_dry_run_log);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const openApproval = () => {
    setActionError(null);
    setShowApprovalModal(true);
  };

  const openRejection = () => {
    setActionError(null);
    setShowRejectModal(true);
  };

  if (loading && !preflight) {
    return (
      <Section title="Database upgrade preflight" testId="database-preflight">
        <LoadingRows label="Running the declarative schema dry run and patch analysis…" rows={3} rowHeight="28px" />
      </Section>
    );
  }

  if (!preflight) {
    return null;
  }

  const destructiveChanges = (preflight.changes || []).filter(
    (c) => c.risk === 'critical' || c.risk === 'high' || c.operation === 'drop_table' || c.operation === 'drop_column'
  );
  const approvedOn = preflight.approved_at ? ` on ${new Date(preflight.approved_at).toLocaleString()}` : '';

  return (
    <Section
      title="Database upgrade preflight"
      tone={preflight.highest_risk === 'critical' ? 'critical' : undefined}
      testId="database-preflight"
      actions={
        <>
          {onRefresh ? (
            <button type="button" className="button button--secondary" onClick={onRefresh} disabled={loading}>
              {loading ? 'Refreshing' : 'Refresh'}
            </button>
          ) : null}
          {preflight.status === 'awaiting_approval' && canManageDeployments ? (
            <>
              <button type="button" className="button button--danger-quiet" onClick={openRejection}>
                Reject upgrade
              </button>
              <button type="button" className="button" onClick={openApproval}>
                Review and approve upgrade
              </button>
            </>
          ) : null}
        </>
      }
    >
      <p className="summary-line">
        <StatusBadge kind="preflight" value={preflight.status} />
        <StatusBadge kind="risk" value={preflight.highest_risk} />
        {preflight.execution_duration_ms > 0 ? (
          <span className="muted tabular-nums">Checked in {preflight.execution_duration_ms.toLocaleString()} ms</span>
        ) : null}
        <code className="muted">setup:upgrade --dry-run=1</code>
      </p>

      {preflight.status === 'awaiting_approval' ? (
        <Callout tone="warning" title="An operator must approve this database upgrade">
          {preflight.risk_summary}
        </Callout>
      ) : null}

      {preflight.status === 'approved' ? (
        <p className="muted" data-testid="preflight-approval">
          Approved by {preflight.approved_by || 'an operator'}
          {approvedOn}
        </p>
      ) : null}

      {preflight.status === 'rejected' ? (
        <Callout tone="danger" title="Upgrade rejected">
          Rejected by {preflight.approved_by || 'an operator'}
          {approvedOn} · Deployment cancelled before the database was changed
        </Callout>
      ) : null}

      {preflight.changes && preflight.changes.length > 0 ? (
        <Subsection title={`Declarative schema changes (${preflight.changes.length})`}>
          <Table label="Declarative schema changes" compact>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Table</th>
                <th scope="col">Column or target</th>
                <th scope="col">Operation</th>
                <th scope="col">Risk</th>
                <th scope="col">Description</th>
                <th scope="col">
                  <span className="visually-hidden">SQL</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {preflight.changes.map((change) => {
                const isExpanded = !!expandedSql[change.id];
                const sqlId = `preflight-sql-${change.id || change.sequence}`;
                return (
                  <React.Fragment key={change.id || change.sequence}>
                    <tr className={change.risk === 'critical' ? 'ssh-row--critical' : undefined}>
                      <td className="muted tabular-nums">{change.sequence}</td>
                      <th scope="row">{change.table_name || '—'}</th>
                      <td>{change.column_name ? <code>{change.column_name}</code> : '—'}</td>
                      <td>
                        <code>{change.operation}</code>
                      </td>
                      <td>
                        <StatusBadge kind="risk" value={change.risk} />
                      </td>
                      <td>{change.description}</td>
                      <td>
                        {change.raw_sql ? (
                          <button
                            type="button"
                            className="button button--ghost button--small"
                            aria-expanded={isExpanded}
                            aria-controls={isExpanded ? sqlId : undefined}
                            onClick={() => toggleSql(change.id)}
                          >
                            {isExpanded ? 'Hide SQL' : 'Show SQL'}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                    {isExpanded && change.raw_sql ? (
                      <tr className="ssh-sql-row">
                        <td colSpan={7}>
                          <pre id={sqlId} className="ssh-code-block">
                            {change.raw_sql}
                          </pre>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Subsection>
      ) : preflight.upgrade_required ? (
        <p className="quiet-empty">No declarative schema changes</p>
      ) : null}

      {preflight.patches && preflight.patches.length > 0 ? (
        <Subsection title={`Detected Magento upgrade code (${preflight.patches.length})`}>
          <Table label="Detected Magento upgrade code" compact>
            <thead>
              <tr>
                <th scope="col">Module</th>
                <th scope="col">Type</th>
                <th scope="col">Path</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {preflight.patches.map((p, idx) => (
                <tr key={p.id || idx}>
                  <th scope="row">
                    <code>{p.module_name}</code>
                  </th>
                  <td>
                    <span className="badge">{p.patch_type.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="font-mono muted">{p.file_path}</td>
                  <td>
                    <StatusBadge kind="gitChange" value={p.git_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Subsection>
      ) : null}

      {preflight.raw_dry_run_log ? (
        <details className="ssh-details">
          <summary>Raw dry-run log ({preflight.raw_dry_run_log.length.toLocaleString()} bytes)</summary>
          <LogPanel
            title="Dry-run log"
            lines={preflight.raw_dry_run_log.split('\n').map((text, index) => ({ key: String(index), text }))}
            toolbar={
              <button type="button" className="button button--secondary button--small" onClick={handleCopyLog}>
                {copySuccess ? 'Copied' : 'Copy log'}
              </button>
            }
          />
        </details>
      ) : null}

      <Dialog
        open={showApprovalModal}
        title="Approve database upgrade"
        description="Commerce Ops takes a backup of the database first, then applies these schema changes."
        onClose={() => setShowApprovalModal(false)}
        busy={actionLoading}
        footer={
          <>
            <button type="button" className="button button--secondary" onClick={() => setShowApprovalModal(false)} disabled={actionLoading}>
              Cancel
            </button>
            <button type="button" className="button" onClick={handleApprove} disabled={!acknowledged || actionLoading}>
              {actionLoading ? 'Approving' : 'Approve and resume deployment'}
            </button>
          </>
        }
      >
        {destructiveChanges.length > 0 ? (
          <Callout tone="danger" role="note" title={`Destructive changes (${destructiveChanges.length})`}>
            <ul>
              {destructiveChanges.map((c) => (
                <li key={c.id || c.sequence}>
                  <code>{c.operation}</code> on <strong>{c.table_name}</strong>
                  {c.column_name ? ` (${c.column_name})` : ''}: {c.description}
                </li>
              ))}
            </ul>
          </Callout>
        ) : null}
        <Field label="Approval note (optional)">
          <Input
            type="text"
            placeholder="e.g. Approved per RFC-402 / Sprint release ticket"
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
          />
        </Field>
        <Checkbox
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          label="I understand that these database changes will be executed on the environment and cannot be undone automatically without a database restore."
        />
        {actionError ? (
          <Callout tone="danger" title="The approval was not saved">
            {actionError}
          </Callout>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={showRejectModal}
        title="Reject database upgrade"
        description="The deployment attempt is cancelled at once. The database is not changed and the new code is not activated."
        confirmLabel="Reject and cancel deployment"
        danger
        busy={actionLoading}
        onConfirm={handleReject}
        onCancel={() => setShowRejectModal(false)}
      >
        <Field label="Reason for rejection">
          <Input
            type="text"
            placeholder="e.g. Destructive table drop requires migration review"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </Field>
        {actionError ? (
          <Callout tone="danger" title="The rejection was not saved">
            {actionError}
          </Callout>
        ) : null}
      </ConfirmDialog>
    </Section>
  );
};
